import { HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/common/Button/Button';
import { Icon, icons } from '@/components/common/Icon';
import { StartupPlan } from '@/components/sections/StartupPlan';

interface PricingFeature {
  text: string;
  included: boolean;
}

interface PricingTier {
  name: string;
  description: string;
  price?: {
    monthly: number;
    annually: number;
  };
  features: PricingFeature[];
  cta: {
    text: string;
    href: string;
  };
  highlighted?: boolean;
}

interface PricingProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  tiers: PricingTier[];
  billingPeriod?: 'monthly' | 'annually';
}

export const Pricing = ({
  title = 'Supercharge your growth',
  description = "Whether you're a small startup, a growing mid-sized business, or a large enterprise, we have plans tailored to your specific demands.",
  tiers,
  billingPeriod = 'monthly',
  className,
  ...props
}: PricingProps) => {
  return (
    <section className={cn('py-16', className)} {...props}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
          <p className="text-lg text-gray-600">{description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={cn(
                'rounded-2xl p-8 bg-white flex flex-col h-full border border-gray-200 hover:shadow-md transition-all duration-300'
              )}
            >
              {/* Header */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold mb-2 text-gray-900">
                  {tier.name}
                </h3>
                <p className="text-gray-600">{tier.description}</p>
              </div>

              {/* Features */}
              <div className="mb-8 flex-grow">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Product</h4>
                <ul className="space-y-4">
                  {tier.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start gap-3 text-gray-600"
                    >
                      <Icon
                        icon={feature.included ? icons.check : icons.remove}
                        className={cn(
                          'w-5 h-5 mt-0.5 flex-shrink-0',
                          feature.included
                            ? 'text-success-600'
                            : 'text-gray-400'
                        )}
                      />
                      <span>{feature.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="mt-auto">
                  <Button
                  variant="solid"
                  color="primary"
                  size="lg"
                  className="w-full"
                  onClick={() => window.open(tier.cta.href, '_blank')}
                >
                  {tier.cta.text}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Startup Plan section */}
        <StartupPlan className="mt-16 mx-auto max-w-6xl px-4" />
      </div>
    </section>
  );
};